import numeral from 'numeral'
import moment from 'moment'
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Text as ChakraText,
  Tooltip,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFillInfoCircleFill as BsFillInfoCircleFillIcon } from 'react-icons/bs'
import { colors } from '../../../../ui-kit'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import { getExtensionFromFileTypeAndMimeType } from '../../../../utils/mediaLibrary'

const FileInfo = ({
  fileDetails,
  isPublicLink,
}: {
  fileDetails: FileDetailsResponse
  isPublicLink?: boolean
}): ReactElement => {
  const { t } = useTranslation()
  const createdBy = fileDetails.createdBy?.name
  const userEmail = fileDetails.createdBy?.email
  const fileExtension = getExtensionFromFileTypeAndMimeType(fileDetails)

  return (
    <AccordionItem borderTopWidth={0} borderBottomWidth={1}>
      <AccordionButton py={6}>
        <Flex
          flex="1"
          textAlign="left"
          lineHeight="1.5em"
          fontWeight="semibold"
          alignItems="center"
          style={{ gap: '1em' }}
        >
          <BsFillInfoCircleFillIcon fontSize="1.5em" />
          {t('file_info', 'File Info')}
        </Flex>
        <AccordionIcon color={colors.appSlate} display="block" />
      </AccordionButton>

      <AccordionPanel>
        <Flex direction="column">
          {fileDetails.width && fileDetails.height ? (
            <Flex marginBottom="10px">
              <ChakraText marginRight="5px">
                {t('Dimensions', 'Dimensions')} :
              </ChakraText>
              <Tooltip label={t('width', 'Width')}>
                <ChakraText as="b">{fileDetails.width}</ChakraText>
              </Tooltip>
              <ChakraText as="b">&nbsp;x&nbsp;</ChakraText>
              <Tooltip label={t('height', 'Height')}>
                <ChakraText as="b">{fileDetails.height}</ChakraText>
              </Tooltip>
            </Flex>
          ) : null}

          <Flex marginBottom="10px">
            <ChakraText marginRight="5px">{t('Size', 'Size')} :</ChakraText>
            <ChakraText as="b">
              {numeral(fileDetails.size).format('0.0 ib').replace('i', '')}
            </ChakraText>
          </Flex>
          {fileExtension && (
            <Flex marginBottom="10px">
              <ChakraText marginRight="5px">
                {t('Format', 'Format')} :
              </ChakraText>
              <ChakraText as="b">{fileExtension.toUpperCase()}</ChakraText>
            </Flex>
          )}
          <Flex marginBottom="10px">
            <ChakraText marginRight="5px">
              {t('Created', 'Created')} :
            </ChakraText>
            <ChakraText as="b">
              {moment(fileDetails.createdAt).format('MMMM Do YYYY') || '-'}
            </ChakraText>
          </Flex>
          {!isPublicLink && createdBy && (
            <Flex marginBottom="10px">
              <ChakraText marginRight="5px">
                {t('creator', 'Creator')} :
              </ChakraText>
              <Tooltip label={userEmail}>
                <ChakraText as="b">{createdBy}</ChakraText>
              </Tooltip>
            </Flex>
          )}
          {
            !isPublicLink ? (
              <Flex marginBottom="10px">
                <ChakraText marginRight="5px">
                  {t('private_file', 'Private file')} :
                </ChakraText>
                <ChakraText as="b">
                  {fileDetails.isPrivateFile ? t('yes', 'Yes') : t('no', 'No')}
                </ChakraText>
              </Flex>
            ) : null
          }
          {
            !isPublicLink ? (
              <Flex marginBottom="10px">
                <ChakraText marginRight="5px">
                  {t('published', 'Published')} :
                </ChakraText>
                <ChakraText as="b">
                {fileDetails.isPublished ? t('yes', 'Yes') : t('no', 'No')}
                </ChakraText>
              </Flex>
            ) : null
          }
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  )
}

export default FileInfo
